import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Save, History, Rocket, ChevronDown, ChevronUp, RotateCcw, Trash2, Plus, ArrowUp, ArrowDown, ExternalLink, CircleDot } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fj = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2380]";

const TYPE_LABELS = {
  section: 'Titolo di sezione',
  text: 'Testo breve',
  textarea: 'Testo lungo',
  email: 'Email',
  tel: 'Telefono',
  date: 'Data',
  number: 'Numero',
  select: 'Menu a tendina',
  radio: 'Scelta singola',
  checkbox: 'Scelte multiple',
  file: 'Allegato file',
  consent: 'Consenso (checkbox)',
};
const HAS_OPTIONS = ['select', 'radio', 'checkbox'];
const ACTION_LABELS = { bozza: 'Bozza salvata', pubblicazione: 'Pubblicazione', ripristino: 'Ripristino' };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');
const newId = () => `d_${Date.now().toString(36)}${Math.floor(Math.random() * 100)}`;

const QuestionEditor = ({ q, idx, total, allFields, onChange, onMove, onDelete, formKey }) => {
  const tid = `fb-${formKey}-${idx}`;
  const set = (k, v) => onChange({ ...q, [k]: v });
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold text-gray-400 uppercase">{idx + 1}. {TYPE_LABELS[q.type] || q.type}</span>
        <div className="flex items-center gap-1">
          <button data-testid={`${tid}-up`} type="button" disabled={idx === 0} onClick={() => onMove(-1)} className="p-1.5 text-gray-400 hover:text-[#1B2380] disabled:opacity-25"><ArrowUp size={14} /></button>
          <button data-testid={`${tid}-down`} type="button" disabled={idx === total - 1} onClick={() => onMove(1)} className="p-1.5 text-gray-400 hover:text-[#1B2380] disabled:opacity-25"><ArrowDown size={14} /></button>
          <button data-testid={`${tid}-del`} type="button" onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-2.5">
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Domanda / etichetta</label>
          <input data-testid={`${tid}-label`} type="text" value={q.label || ''} onChange={(e) => set('label', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipo di campo</label>
          <select data-testid={`${tid}-type`} value={q.type} onChange={(e) => set('type', e.target.value)} className={inputCls}>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      {HAS_OPTIONS.includes(q.type) && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">Opzioni (una per riga)</label>
          <textarea data-testid={`${tid}-options`} rows={3} value={(q.options || []).join('\n')}
            onChange={(e) => set('options', e.target.value.split('\n'))}
            onBlur={(e) => set('options', e.target.value.split('\n').map((o) => o.trim()).filter(Boolean))}
            className={`${inputCls} resize-y`} />
        </div>
      )}
      {!['section', 'file', 'consent'].includes(q.type) && (
        <div className="grid sm:grid-cols-2 gap-2.5">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Suggerimento sotto la domanda (facoltativo)</label>
            <input data-testid={`${tid}-hint`} type="text" value={q.hint || ''} onChange={(e) => set('hint', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Esempio nel campo (facoltativo)</label>
            <input data-testid={`${tid}-placeholder`} type="text" value={q.placeholder || ''} onChange={(e) => set('placeholder', e.target.value)} className={inputCls} />
          </div>
        </div>
      )}
      {q.type !== 'section' && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input data-testid={`${tid}-required`} type="checkbox" checked={!!q.required} onChange={(e) => set('required', e.target.checked)} className="w-4 h-4 accent-[#1B2380]" />
            Obbligatoria
          </label>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            Mostra solo se
            <select data-testid={`${tid}-showif-field`} value={q.show_if?.field || ''} className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
              onChange={(e) => set('show_if', e.target.value ? { field: e.target.value, equals: q.show_if?.equals || '' } : undefined)}>
              <option value="">— sempre visibile —</option>
              {allFields.filter((f) => f.id !== q.id && !['section', 'file'].includes(f.type)).map((f) => <option key={f.id} value={f.id}>{(f.label || f.id).slice(0, 40)}</option>)}
            </select>
            {q.show_if?.field && (() => {
              const target = allFields.find((f) => f.id === q.show_if.field);
              const opts = target?.options?.filter(Boolean);
              return (
                <>
                  =
                  {opts && opts.length > 0 ? (
                    <select data-testid={`${tid}-showif-equals`} value={q.show_if.equals || ''}
                      onChange={(e) => set('show_if', { ...q.show_if, equals: e.target.value })}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs max-w-[160px]">
                      <option value="">— scegli valore —</option>
                      {opts.map((o) => <option key={o} value={o}>{o.slice(0, 35)}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`${tid}-showif-equals`} type="text" value={q.show_if.equals || ''} placeholder="valore"
                      onChange={(e) => set('show_if', { ...q.show_if, equals: e.target.value })}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs w-28" />
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

const FormEditor = ({ form, onReload }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(form.draft);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => { setData(form.draft); }, [form.draft]);

  const setFields = (fields) => setData((p) => ({ ...p, fields }));
  const fields = data.fields || [];

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await fj(`/api/forms/admin/${form.key}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data }),
      });
      const out = await res.json();
      if (res.ok) { toast.success(out.message); setHistory(null); onReload(); }
      else toast.error(typeof out.detail === 'string' ? out.detail : 'Errore nel salvataggio');
    } catch { toast.error('Errore di connessione'); } finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await fj(`/api/forms/admin/${form.key}/publish`, { method: 'POST' });
      const out = await res.json();
      if (res.ok) { toast.success(out.message); setHistory(null); onReload(); }
      else toast.error(typeof out.detail === 'string' ? out.detail : 'Errore nella pubblicazione');
    } catch { toast.error('Errore di connessione'); } finally { setPublishing(false); }
  };

  const loadHistory = async () => {
    if (history) { setHistory(null); return; }
    try {
      const res = await fj(`/api/forms/admin/${form.key}/history`);
      const out = await res.json();
      setHistory(out.versions || []);
    } catch { toast.error('Errore di connessione'); }
  };

  const restore = async (vid) => {
    try {
      const res = await fj(`/api/forms/admin/${form.key}/restore/${vid}`, { method: 'POST' });
      const out = await res.json();
      if (res.ok) { toast.success(out.message); setData(out.draft); setHistory(null); onReload(); }
      else toast.error(typeof out.detail === 'string' ? out.detail : 'Errore');
    } catch { toast.error('Errore di connessione'); }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl">
      <button data-testid={`fb-toggle-${form.key}`} type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <div>
          <p className="text-sm font-semibold text-gray-800">{form.label}</p>
          <p className="text-xs text-gray-500">{(form.draft.fields || []).filter((f) => f.type !== 'section').length} domande</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {form.has_unpublished ? (
            <span data-testid={`fb-status-${form.key}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><CircleDot size={11} /> Modifiche non pubblicate</span>
          ) : form.is_published ? (
            <span data-testid={`fb-status-${form.key}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#0E8C84]/15 text-[#0B7770]">Pubblicato {fmtDate(form.published_at)}</span>
          ) : (
            <span data-testid={`fb-status-${form.key}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Configurazione iniziale</span>
          )}
          {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200 pt-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Titolo del modulo</label>
              <input data-testid={`fb-${form.key}-title`} type="text" value={data.title || ''} onChange={(e) => setData((p) => ({ ...p, title: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sottotitolo</label>
              <input data-testid={`fb-${form.key}-subtitle`} type="text" value={data.subtitle || ''} onChange={(e) => setData((p) => ({ ...p, subtitle: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Messaggio dopo l'invio</label>
              <input data-testid={`fb-${form.key}-success`} type="text" value={data.success_message || ''} onChange={(e) => setData((p) => ({ ...p, success_message: e.target.value }))} className={inputCls} />
            </div>
          </div>

          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Domande</p>
          <div className="space-y-2.5">
            {fields.map((q, idx) => (
              <QuestionEditor key={q.id} q={q} idx={idx} total={fields.length} allFields={fields} formKey={form.key}
                onChange={(nq) => setFields(fields.map((f, i) => (i === idx ? nq : f)))}
                onMove={(dir) => {
                  const nf = [...fields];
                  [nf[idx], nf[idx + dir]] = [nf[idx + dir], nf[idx]];
                  setFields(nf);
                }}
                onDelete={() => setFields(fields.filter((_, i) => i !== idx))}
              />
            ))}
          </div>
          <button data-testid={`fb-${form.key}-add`} type="button"
            onClick={() => setFields([...fields, { id: newId(), type: 'text', label: '', required: false }])}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B2380] border border-dashed border-[#1B2380]/40 rounded-lg px-3 py-2 hover:bg-[#1B2380]/5">
            <Plus size={13} /> Aggiungi domanda
          </button>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button data-testid={`fb-save-${form.key}`} type="button" onClick={saveDraft} disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#1B2380] text-[#1B2380] hover:bg-[#1B2380]/5 text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
              <Save size={14} /> {saving ? 'Salvataggio...' : 'Salva bozza'}
            </button>
            <button data-testid={`fb-publish-${form.key}`} type="button" onClick={publish} disabled={publishing}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0E8C84] hover:bg-[#0B7770] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
              <Rocket size={14} /> {publishing ? 'Pubblicazione...' : 'Pubblica il modulo'}
            </button>
            <button data-testid={`fb-history-${form.key}`} type="button" onClick={loadHistory}
              className="inline-flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-200 transition-colors">
              <History size={14} /> Storico
            </button>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            <a data-testid={`fb-preview-draft-${form.key}`} href={`/moduli/anteprima/${form.key}?bozza=1`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#E67E22] hover:underline">
              <ExternalLink size={12} /> Prova la bozza
            </a>
            <a data-testid={`fb-preview-pub-${form.key}`} href={`/moduli/anteprima/${form.key}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#1B2380] hover:underline">
              <ExternalLink size={12} /> Vedi la versione pubblicata (invio reale)
            </a>
          </div>

          {history && (
            <div className="space-y-1.5" data-testid={`fb-history-list-${form.key}`}>
              {history.length === 0 && <p className="text-xs text-gray-400">Nessuna versione salvata finora.</p>}
              {history.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-600">{ACTION_LABELS[v.action] || v.action} — {v.by} — {fmtDate(v.at)}</p>
                  <button data-testid={`fb-restore-${v.id}`} type="button" onClick={() => restore(v.id)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B2380] hover:underline flex-shrink-0">
                    <RotateCcw size={12} /> Ripristina nella bozza
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminModuli() {
  const [forms, setForms] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await fj('/api/forms/admin/all');
      if (res.ok) {
        const out = await res.json();
        setForms(out.forms || []);
      }
    } catch { /* silenzioso */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="pt-8 border-t border-gray-200 space-y-4">
      <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><ClipboardList size={18} className="text-[#E67E22]" /> Costruttore moduli</h2>
      <p className="text-sm text-gray-500">Modifica le domande dei questionari: aggiungi, riordina, rendi obbligatorie o condizionali. Bozza → prova → Pubblica. Le richieste inviate dai moduli arrivano via email all'Agenzia. I moduli attuali del sito restano attivi finché non deciderai di sostituirli con questi.</p>
      {forms.map((f) => <FormEditor key={f.key} form={f} onReload={load} />)}
    </div>
  );
}
